import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";
import { Doughnut, Bar } from "react-chartjs-2";
import "./sip-calculator.css";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement
);

const formatINR = (amount) =>
  Number(amount.toFixed(0)).toLocaleString("en-IN");

function computeSIP(monthlyInvestment, rate, years) {
  if (monthlyInvestment <= 0 || rate <= 0 || years <= 0) return null;

  const n = years * 12;
  const r = Math.pow(1 + rate / 100, 1 / 12) - 1;
  const totalValue =
    monthlyInvestment * ((Math.pow(1 + r, n) - 1) / r) * (1 + r);
  const investedAmount = monthlyInvestment * n;
  const gain = totalValue - investedAmount;

  let value = 0;
  let investedSoFar = 0;
  const growthData = [];
  for (let m = 1; m <= n; m++) {
    value = (value + monthlyInvestment) * (1 + r);
    investedSoFar += monthlyInvestment;
    if (m % 12 === 0) {
      growthData.push({
        year: m / 12,
        invested: Number(investedSoFar.toFixed(0)),
        returns: Number((value - investedSoFar).toFixed(0)),
      });
    }
  }

  return {
    investedAmount: Number(investedAmount.toFixed(0)),
    gain: Number(gain.toFixed(0)),
    totalValue: Number(totalValue.toFixed(0)),
    growthData,
  };
}

export default function SIPCalculator() {
  const [monthlyInvestment, setMonthlyInvestment] = useState(5000);
  const [rate, setRate] = useState(12);
  const [years, setYears] = useState(10);

  const results = useMemo(
    () => computeSIP(monthlyInvestment, rate, years),
    [monthlyInvestment, rate, years]
  );

  const doughnutData = results && {
    labels: ["Invested Amount", "Est. Returns"],
    datasets: [
      {
        data: [results.investedAmount, results.gain],
        backgroundColor: ["#139bb2", "#a8e4ec"],
        borderWidth: 0,
        hoverOffset: 4,
      },
    ],
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "68%",
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          padding: 16,
          color: "#64748b",
          font: { size: 12, weight: "600" },
        },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => ` ₹${formatINR(ctx.parsed)}`,
        },
      },
    },
  };

  const barData = results && {
    labels: results.growthData.map((d) => `Y${d.year}`),
    datasets: [
      {
        label: "Invested",
        data: results.growthData.map((d) => d.invested),
        backgroundColor: "#139bb2",
        borderRadius: 4,
        stack: "growth",
      },
      {
        label: "Returns",
        data: results.growthData.map((d) => d.returns),
        backgroundColor: "#a8e4ec",
        borderRadius: 4,
        stack: "growth",
      },
    ],
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ₹${formatINR(ctx.parsed.y)}`,
        },
      },
      legend: {
        position: "top",
        align: "end",
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          color: "#64748b",
          font: { size: 12, weight: "600" },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
        ticks: { color: "#94a3b8", font: { size: 11 } },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        grid: { color: "#eef2f5" },
        ticks: {
          color: "#94a3b8",
          font: { size: 11 },
          callback: (v) => `₹${formatINR(v)}`,
        },
      },
    },
    interaction: { mode: "index", intersect: false },
  };

  return (
    <div className="sip-page">
      <section className="sip-hero">
        <div className="sip-hero-blob" aria-hidden="true" />
        <div className="sip-hero-inner">
          <p className="sip-eyebrow">SIP CALCULATOR</p>
          <h1 className="sip-hero-title">
            Plan Your Wealth with <span>Monthly SIPs</span>
          </h1>
          <p className="sip-hero-lead">
            Estimate how your systematic investments can grow over time. Adjust
            amount, return rate, and tenure to see projected corpus instantly.
          </p>
        </div>
      </section>

      <section className="sip-calc">
        <div className="sip-calc-inner">
          <div className="sip-calc-grid">
            <div className="sip-panel">
              <h2 className="sip-panel-title">Investment Details</h2>

              <div className="sip-field">
                <div className="sip-field-head">
                  <label htmlFor="sip-monthly">Monthly Investment</label>
                  <div className="sip-field-value">
                    <span>₹</span>
                    <input
                      id="sip-monthly"
                      type="number"
                      min={500}
                      max={500000}
                      step={500}
                      value={monthlyInvestment}
                      onChange={(e) =>
                        setMonthlyInvestment(Number(e.target.value) || 0)
                      }
                    />
                  </div>
                </div>
                <input
                  type="range"
                  className="sip-range"
                  min={500}
                  max={100000}
                  step={500}
                  value={Math.min(monthlyInvestment, 100000)}
                  onChange={(e) =>
                    setMonthlyInvestment(Number(e.target.value))
                  }
                  aria-label="Monthly investment slider"
                />
                <div className="sip-range-ends">
                  <span>₹500</span>
                  <span>₹1,00,000</span>
                </div>
              </div>

              <div className="sip-field">
                <div className="sip-field-head">
                  <label htmlFor="sip-rate">Expected Return (p.a.)</label>
                  <div className="sip-field-value">
                    <input
                      id="sip-rate"
                      type="number"
                      min={1}
                      max={30}
                      step={0.5}
                      value={rate}
                      onChange={(e) => setRate(Number(e.target.value) || 0)}
                    />
                    <span>%</span>
                  </div>
                </div>
                <input
                  type="range"
                  className="sip-range"
                  min={1}
                  max={30}
                  step={0.5}
                  value={rate}
                  onChange={(e) => setRate(Number(e.target.value))}
                  aria-label="Expected return slider"
                />
                <div className="sip-range-ends">
                  <span>1%</span>
                  <span>30%</span>
                </div>
              </div>

              <div className="sip-field">
                <div className="sip-field-head">
                  <label htmlFor="sip-years">Time Period</label>
                  <div className="sip-field-value">
                    <input
                      id="sip-years"
                      type="number"
                      min={1}
                      max={40}
                      step={1}
                      value={years}
                      onChange={(e) => setYears(Number(e.target.value) || 0)}
                    />
                    <span>Yr</span>
                  </div>
                </div>
                <input
                  type="range"
                  className="sip-range"
                  min={1}
                  max={40}
                  step={1}
                  value={years}
                  onChange={(e) => setYears(Number(e.target.value))}
                  aria-label="Time period slider"
                />
                <div className="sip-range-ends">
                  <span>1 Yr</span>
                  <span>40 Yr</span>
                </div>
              </div>

              <div className="sip-cta-row">
                <Link to="/mutual-fund" className="sip-btn sip-btn--primary">
                  Explore Mutual Funds
                </Link>
                <Link to="/contact" className="sip-btn sip-btn--ghost">
                  Talk to an Advisor
                </Link>
              </div>
            </div>

            <div className="sip-panel sip-panel--results">
              <h2 className="sip-panel-title">Projected Returns</h2>

              {results ? (
                <>
                  <div className="sip-results-top">
                    <div className="sip-donut-wrap">
                      <Doughnut data={doughnutData} options={doughnutOptions} />
                    </div>
                    <div className="sip-stats">
                      <div className="sip-stat">
                        <span className="sip-stat-label">Invested Amount</span>
                        <strong>₹{formatINR(results.investedAmount)}</strong>
                      </div>
                      <div className="sip-stat">
                        <span className="sip-stat-label">Est. Returns</span>
                        <strong className="sip-stat--teal">
                          ₹{formatINR(results.gain)}
                        </strong>
                      </div>
                      <div className="sip-stat sip-stat--total">
                        <span className="sip-stat-label">Total Value</span>
                        <strong>₹{formatINR(results.totalValue)}</strong>
                      </div>
                    </div>
                  </div>

                  <p className="sip-summary">
                    A monthly SIP of{" "}
                    <strong>₹{formatINR(monthlyInvestment)}</strong> for{" "}
                    <strong>
                      {years} {years > 1 ? "years" : "year"}
                    </strong>{" "}
                    at <strong>{rate}%</strong> p.a. can grow to{" "}
                    <strong>₹{formatINR(results.totalValue)}</strong>, including{" "}
                    <strong>₹{formatINR(results.gain)}</strong> in estimated
                    returns.
                  </p>
                </>
              ) : (
                <p className="sip-empty">
                  Enter valid positive values to see your projection.
                </p>
              )}
            </div>
          </div>

          {results && (
            <div className="sip-panel sip-panel--chart">
              <div className="sip-chart-head">
                <h2 className="sip-panel-title">Yearly Growth</h2>
                <p>Invested amount vs estimated returns over time</p>
              </div>
              <div className="sip-bar-wrap">
                <Bar data={barData} options={barOptions} />
              </div>
            </div>
          )}

          <div className="sip-related">
            <p>Try our other calculators</p>
            <div className="sip-related-links">
              <Link to="/LumpsumCalculator">Lumpsum</Link>
              <Link to="/RetirementCalculator">Retirement</Link>
              <Link to="/emi-calculator">EMI</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
